import { Group, Loader, Stack, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { ClaudeMessage, Conversation } from '~api';
import { formatElapsedSeconds, MINUTE_MS } from './elapsed-format';

type OperationLineProps = {
  call: ClaudeMessage.C.ToolUse;
  result: Conversation.ToolOutputEvent<Date, string> | null;
  /** ISO start of the model call that authored this tool call (see `NarrationGroup`). */
  stepStart: string | undefined;
};

/**
 * @desc Humanised duration: sub-second in ms, then seconds to one decimal, then minutes and
 * seconds. The decimal is dropped at the minute — a step whose tenths matter is not also a step
 * that ran for two minutes, and `128.4s` is the reading the switch exists to avoid.
 */
export const formatTiming = (ms?: number): string => {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < MINUTE_MS) return `${(ms / 1000).toFixed(1)}s`;
  return formatElapsedSeconds(Math.round(ms / 1000));
};

/**
 * @desc Wall time of the step as the user experienced it: from the model call that authored
 * the tool call through to the tool finishing. The model spends seconds writing a script the
 * sandbox then runs in milliseconds, so execution time alone under-reports the step by orders
 * of magnitude — that figure is kept as secondary detail rather than shown as the headline.
 *
 * Returns undefined when either end is missing (conversations predating modelCall metadata,
 * healed synthetic tool outputs, results still in flight) or when the arithmetic is not
 * trustworthy — an unparseable stamp, or a span shorter than the execution it contains,
 * which means the two stamps did not come from the same step.
 */
export const stepElapsedMs = (
  stepStart: string | undefined,
  tFinish: string | undefined,
  execMs: number | undefined,
): number | undefined => {
  if (!stepStart || !tFinish) return undefined;

  const elapsed = new Date(tFinish).getTime() - new Date(stepStart).getTime();
  if (!Number.isFinite(elapsed) || elapsed <= 0) return undefined;
  if (execMs !== undefined && elapsed < execMs) return undefined;

  return elapsed;
};

/**
 * @desc The family a tool belongs to, as the user knows it. This is the row's label when the
 * call carries no description of its own — a call that fails before the model has written one
 * (a malformed input, a sandbox that never started) must still say *something*, and the
 * internal tool id is never that something.
 */
const TOOL_FAMILIES: Record<string, string> = {
  python_executor: 'Python',
  load_skill: 'Skill',
};

/** @desc Last-resort label for a tool with no family of its own: `artifact_reader` → `Artifact reader`. */
const humaniseToolName = (name: string): string => {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : 'Tool';
};

/** @desc How much of a failure reason fits on one row before it stops being a glance. */
const REASON_MAX_CHARS = 120;

/** A traceback's opening line: a label for what follows, never the failure itself. */
const TRACEBACK_HEADER = /^Traceback \(most recent call last\):$/;
/** Structural punctuation from a pretty-printed payload — a line that carries no sentence. */
const PUNCTUATION_ONLY = /^[[\]{}(),'"`]*$/;

/**
 * @desc The messages out of a rejected tool input. A schema rejection reaches us as
 * `BackendError.from(zodError).message`, which is a pretty-printed array of issues — its first
 * line is a bare `[` and its last a bare `]`, so neither end of the text says anything.
 */
const issueMessages = (error: string): string | undefined => {
  if (!error.trimStart().startsWith('[')) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(error);
  } catch {
    // Not JSON after all — the line-based reading below covers it.
    return undefined;
  }
  if (!Array.isArray(parsed)) return undefined;

  const messages = parsed.flatMap((issue: unknown) =>
    issue && typeof issue === 'object' && 'message' in issue && typeof issue.message === 'string'
      ? [issue.message]
      : [],
  );

  return messages.length > 0 ? messages.join('; ') : undefined;
};

/**
 * @desc The line of a multi-line failure that names it. A Python traceback puts the sentence
 * that matters — `KeyError: 'market_id'` — *last*, under a header and the frames that led
 * there; reading from the top yields "Traceback (most recent call last):", which is true of
 * every traceback and therefore tells the user nothing.
 */
const failingLine = (error: string): string | undefined => {
  const lines = error
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const [first] = lines;
  if (lines.length <= 1) return first;

  const meaningful = lines.filter(
    (line) => !TRACEBACK_HEADER.test(line) && !PUNCTUATION_ONLY.test(line),
  );

  return meaningful[meaningful.length - 1] ?? first;
};

/**
 * @desc A failure reduced to one muted row: the sentence that names it, whitespace-collapsed and
 * truncated. The whole text stays available as the row's title.
 */
export const summariseError = (error: string | null): string | undefined => {
  if (!error) return undefined;

  const reason = issueMessages(error) ?? failingLine(error);
  const line = reason?.replace(/\s+/g, ' ').trim();
  if (!line) return undefined;

  return line.length > REASON_MAX_CHARS ? `${line.slice(0, REASON_MAX_CHARS - 1)}…` : line;
};

export function OperationLine({ call, result, stepStart }: OperationLineProps) {
  // Determine status
  const isCompleted = result !== null;
  const hasError = isCompleted && result.payload.error !== null;
  const execTiming = result?.metadata?.tool?.tElapsed;
  const stepTiming = stepElapsedMs(stepStart, result?.metadata?.tool?.tFinish, execTiming);

  // The step time is what the user watched; execution time stays available on hover.
  const timing = stepTiming ?? execTiming;
  const timingTitle =
    stepTiming && execTiming
      ? `model+tool step ${formatTiming(stepTiming)}; tool execution ${formatTiming(execTiming)}`
      : undefined;

  // Generate summary for completed operations
  const getSummary = (): string | null => {
    if (!isCompleted || !result) return null;

    const parts: string[] = [];

    // Add timing
    if (timing) {
      parts.push(formatTiming(timing));
    }

    // A failure has no items to describe — it reports why it failed instead, on the same row
    // the elapsed time would have used.
    if (hasError) {
      const reason = summariseError(result.payload.error);
      if (reason) parts.push(reason);
      return parts.length > 0 ? parts.join(' • ') : null;
    }

    // Add output items summary
    const items = result.payload.items || [];
    if (items.length > 0) {
      // Count charts
      const chartCount = items.filter((item) => item.format === 'png').length;

      // Get non-chart items
      const nonChartItems = items
        .filter((item) => item.format !== 'png' && item.format !== 'markdown')
        .map((item) => {
          if (item.format === 'csv' && item.summary?.shape?.rows) {
            const { rows, columns } = item.summary.shape;
            return `CSV: ${rows} rows x ${columns} columns`;
          }
          if (item.format === 'json' && item.summary?.shape?.items) {
            const { items } = item.summary.shape;
            if (!items) return 'No results';
            return `${items} result`.concat(items > 1 ? 's' : '');
          }
          return item.format;
        });

      const summaries: string[] = [];

      // Add chart summary
      if (chartCount > 0) {
        summaries.push(chartCount === 1 ? 'chart' : `Generated ${chartCount} charts`);
      }

      // Add markdown summary (model-context-only markdown is not a displayed response)
      const hasMarkdown = items.some(
        (item) => item.format === 'markdown' && item.summary?.display !== false,
      );
      if (hasMarkdown) {
        summaries.push('Generated response');
      }

      // Add other items
      summaries.push(...nonChartItems);

      if (summaries.length > 0) {
        parts.push(summaries.join(', '));
      }
    }

    return parts.length > 0 ? parts.join(' • ') : null;
  };

  // Status icon — an element, not a component declared in render (which would remount each render)
  const statusIcon = !isCompleted ? (
    <Loader size={14} color="gray" />
  ) : hasError ? (
    <IconX size={14} color="var(--mantine-color-red-6)" />
  ) : (
    <IconCheck size={14} color="var(--mantine-color-green-6)" />
  );

  // What the model said this call was for, when it said anything at all
  const description = (() => {
    if (!call.input || typeof call.input !== 'object') return undefined;
    if ('description' in call.input) return call.input.description as string;
    if (call.name === 'load_skill' && 'skill' in call.input) return call.input.skill as string;
    return undefined;
  })();

  const family = TOOL_FAMILIES[call.name];
  const label = description
    ? family
      ? `${family}: ${description}`
      : description
    : (family ?? humaniseToolName(call.name));
  const summary = getSummary();
  // A truncated reason keeps its full text on hover; a successful row keeps the timing detail.
  const detailTitle = hasError ? (result.payload.error ?? undefined) : timingTitle;

  return (
    <Stack gap={4}>
      <Group gap="xs" wrap="nowrap">
        {statusIcon}
        <Text size="sm" c="dimmed" flex={1}>
          {label}
        </Text>
      </Group>
      {summary && (
        <Group gap={6} wrap="nowrap" pl={22}>
          <Text size="xs" c="dimmed" lh={1}>
            ├─
          </Text>
          <Text size="xs" c="dimmed" title={detailTitle}>
            {summary}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
